import { useRef, useState } from "react";
import { Image as ImageIcon, Loader2, Upload, Trash2 } from "lucide-react";
import { toast } from "sonner";
import { api, errMsg, storeLogoUrl } from "@/api";

const MAX_BYTES = 2 * 1024 * 1024;
const OK_TYPES = ["image/png", "image/jpeg", "image/webp"];

// Logo per toko (terikat store_id). Ganti Logo = fitur Paket PRO; superadmin hanya melihat status.
export function LogoPanel({ store, readOnly = false, onSaved }) {
  const fileRef = useRef(null);
  const [file, setFile] = useState(null);
  const [preview, setPreview] = useState("");
  const [busy, setBusy] = useState("");
  const isPro = store?.plan_tier === "pro";
  const logoUrl = storeLogoUrl(store);

  const pick = (e) => {
    const f = e.target.files?.[0];
    setFile(null); setPreview("");
    if (!f) return;
    if (!OK_TYPES.includes(f.type)) { toast.error("Format logo harus PNG, JPG, JPEG, atau WEBP"); return; }
    if (f.size > MAX_BYTES) { toast.error("Ukuran logo maksimal 2 MB"); return; }
    setFile(f);
    setPreview(URL.createObjectURL(f));
  };

  const save = async () => {
    if (!file) return;
    setBusy("save");
    try {
      const fd = new FormData();
      fd.append("file", file);
      await api.post("/admin/store/logo", fd, { params: store?.id ? { store_id: store.id } : {} });
      toast.success("Logo toko disimpan");
      setFile(null); setPreview("");
      if (fileRef.current) fileRef.current.value = "";
      onSaved?.();
    } catch (e) { toast.error(errMsg(e, "Gagal menyimpan logo")); }
    finally { setBusy(""); }
  };

  const remove = async () => {
    setBusy("del");
    try {
      await api.delete("/admin/store/logo", { params: store?.id ? { store_id: store.id } : {} });
      toast.success("Logo dihapus — kembali ke logo default");
      onSaved?.();
    } catch (e) { toast.error(errMsg(e)); }
    finally { setBusy(""); }
  };

  return (
    <div data-testid="logo-panel" style={{ maxWidth: 520 }}>
      <h3 className="section-title" style={{ marginTop: 0, display: "flex", alignItems: "center", gap: 6 }}>
        <ImageIcon size={18} /> Logo Toko
        <span style={{ fontSize: 12, fontWeight: 700, padding: "2px 8px", borderRadius: 999, background: isPro ? "#dcfce7" : "#f1f5f9", color: isPro ? "#166534" : "#475569" }} data-testid="logo-plan-badge">
          Paket {isPro ? "PRO" : "STANDAR"}
        </span>
      </h3>

      <div style={{ display: "flex", alignItems: "center", gap: 14, margin: "10px 0 14px" }}>
        {logoUrl
          ? <img src={logoUrl} alt={`Logo ${store.store_name}`} className="store-logo" style={{ width: 72, height: 72 }} data-testid="logo-current" />
          : <div className="store-logo store-logo-default" style={{ width: 72, height: 72, fontSize: 30 }} data-testid="logo-current-default">{(store.store_name || "?").trim().charAt(0).toUpperCase()}</div>}
        <div className="muted-sm" data-testid="logo-status-text">
          {store.logo_path ? "Logo kustom toko ini sedang dipakai di halaman toko." : "Belum ada logo — memakai branding default sistem."}
        </div>
      </div>

      {readOnly ? (
        <p className="muted-sm" data-testid="logo-readonly-note">Superadmin mengatur paket toko di menu <strong>Admin &amp; Toko</strong>. Penggantian logo dilakukan oleh admin/pemilik toko.</p>
      ) : !isPro ? (
        <p className="muted-sm" data-testid="logo-pro-locked" style={{ background: "#fef3c7", border: "1px solid #fde68a", color: "#92400e", borderRadius: 10, padding: "10px 12px" }}>
          Fitur <strong>Ganti Logo</strong> khusus <strong>Paket PRO</strong>. Toko ini memakai Paket STANDAR sehingga menggunakan logo default. {store.logo_path ? "Logo lama tetap tersimpan dan akan aktif kembali saat upgrade ke PRO. " : ""}Hubungi penyedia layanan untuk upgrade ke PRO.
        </p>
      ) : (
        <div data-testid="logo-upload-box">
          <label className="field-label">Pilih file logo (PNG, JPG, JPEG, WEBP — maks. 2 MB)</label>
          <input ref={fileRef} type="file" accept="image/png,image/jpeg,image/webp" onChange={pick} className="text-input" data-testid="logo-file-input" />
          {preview && (
            <div style={{ margin: "10px 0" }} data-testid="logo-preview-box">
              <div className="field-label" style={{ marginBottom: 4 }}>Preview Logo</div>
              <img src={preview} alt="Preview logo" className="logo-preview-img" data-testid="logo-preview" />
              <div className="muted-sm" style={{ marginTop: 4 }}>{file?.name} · {(file.size / 1024).toFixed(0)} KB</div>
            </div>
          )}
          <div className="actions-right" style={{ marginTop: 10 }}>
            {store.logo_path && (
              <button className="btn-ghost danger-text" onClick={remove} disabled={!!busy} data-testid="logo-delete-btn">
                {busy === "del" ? <Loader2 className="spin" size={14} /> : <Trash2 size={14} />} Hapus Logo
              </button>
            )}
            <button className="btn-primary" onClick={save} disabled={!file || !!busy} data-testid="logo-save-btn">
              {busy === "save" ? <Loader2 className="spin" size={16} /> : <Upload size={16} />} Simpan Logo
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
